import { Button, Icon, Image, Modal, Typography } from '@smartgift/frontend-toolkit';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Card } from '@hero/atoms';
import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { APPROVAL_STATUSES, PRODUCT_TYPES } from '@hero/constants';
import { useGetMeConfigs, useShowEGiftCardRequireNotification } from '@hero/hooks';
import { ShowMore, ModalValidationErrors } from '@hero/molecules';
import { useSendToApproval } from '@hero/services/organization-service/hooks';
import { useNotification } from '@hero/store/hooks';
import { getSafeInnerHtml } from '@hero/utils';

import { DonationOptions } from '../Charity/DonationOptions';
import { ProductDetailInfo } from '../ProductDetail/ProductDetailInfo';

import * as S from './style';

function SecondaryProductDetailModal({
  isOpen,
  onRequestClose,
  onBackClick,
  onUpdateProduct,
  onOk,
  product,
  isSelectedProduct = false, // product selected before
  initialSelectedSkus = [], // if product selected before it has sku selection
  isProductsRemovable = true,
  featureType,
  secondaryType,
  isDraft
}) {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { addModalError } = useValidationErrorsStore();
  const { giftCardStatus } = useGetMeConfigs();
  const { showEGiftCardNotification } = useShowEGiftCardRequireNotification();
  const { mutate: sendToApproval } = useSendToApproval();
  const { recognitionCode, code } = useParams();

  const pageLayoutContentRef = React.useRef(null);

  const ALLOWED_FEATURE_TYPES = ['recognition', 'automation'];

  const defaultSkuSelectionState = product?.skus?.length > 1 ? [] : product?.skus;
  const [selectedSkus, setSelectedSkus] = useState(
    (isSelectedProduct ? initialSelectedSkus : defaultSkuSelectionState) ?? []
  );

  if (isEmpty(product)) {
    return null;
  }

  const MODAL_CONSTANTS = {
    charity: {
      primaryButtonChildren: 'includeThisDonation'
    },
    'gift-card': {
      primaryButtonChildren: 'includeThisGift'
    }
  };

  const checkCustomErrors = async () => {
    let hasCustomError = false;
    if (selectedSkus?.length === 0) {
      await addModalError(
        <Trans
          key="noSelectedCharity"
          i18nKey="errors.noSelectedCharity"
          components={{ b: <b /> }}
        />
      );
      hasCustomError = true;
    }
    return hasCustomError;
  };

  const onPrimaryButtonClick = async () => {
    const hasCustomError = await checkCustomErrors();
    if (!hasCustomError) {
      if (isSelectedProduct) {
        onUpdateProduct({
          ...product,
          meta: {
            ...product.meta,
            productType: secondaryType
          },
          skus: selectedSkus
        });
        onRequestClose();
      } else {
        onOk({
          ...product,
          meta: {
            ...product.meta,
            productType: secondaryType
          },
          skus: selectedSkus,
          removeItem: true
        });
      }

      onRequestClose();
    }
    if (!isDraft && ALLOWED_FEATURE_TYPES.includes(featureType)) {
      if (giftCardStatus === APPROVAL_STATUSES.approval_required) {
        showEGiftCardNotification();
      }
      if (giftCardStatus === APPROVAL_STATUSES.pending_approval) {
        sendToApproval({
          approveType: PRODUCT_TYPES.GIFT_CARD,
          payload: {
            associationCode: recognitionCode || code,
            associationType: featureType
          }
        });
      }
    }
  };

  const showRemoveItemPrompt = () => {
    onRequestClose();
    showNotification({
      title: t('removeItemPromptQuestion', { featureName: featureType }),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      primaryButtonProps: {
        color: 'error',
        children: t('removeItem')
      },
      secondaryButtonProps: { children: t('keepItem') },
      onSubmit: () =>
        onOk({
          ...product,
          skus: selectedSkus,
          removeItem: true
        })
    });
  };

  const showTermsAndConditionDialog = () => {
    showNotification({
      header: t('eGiftCardTermsAndConditions'),
      message: (
        <div {...getSafeInnerHtml(product.meta?.incommAssets?.termsconditions?.text || '')} />
      ),
      primaryButtonProps: { onClick: onBackClick }
    });
  };

  const showPurchaseDisclaimerDialog = () => {
    showNotification({
      header: t('eGiftCardPurchaseDisclaimer'),
      message: (
        <div {...getSafeInnerHtml(product.meta?.incommAssets?.legaldisclaimer?.text || '')} />
      ),
      primaryButtonProps: { onClick: onBackClick }
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={t('itemDetails')}
      primaryButtonProps={{
        children: t(MODAL_CONSTANTS[secondaryType].primaryButtonChildren),
        onClick: onPrimaryButtonClick,
        color: 'primary'
      }}
      secondaryButtonProps={
        isSelectedProduct
          ? {
              color: 'error',
              variant: 'outlined',
              children: t('removeItem'),
              disabled: !isProductsRemovable,
              onClick: showRemoveItemPrompt
            }
          : {
              children: t('cancel'),
              onClick: onRequestClose
            }
      }
      onBack={onBackClick}
      size="xl"
    >
      <ModalValidationErrors pageLayoutContentRef={pageLayoutContentRef} />
      <S.SecondaryModalBody>
        <S.ImageColumn>
          <Card>
            <Image src={product?.images[0].url} alt={product.name} style={{ maxWidth: 476 }} />
          </Card>
          <S.ProductDesc>
            <ShowMore numberOfLines={1} buttonColor="fg">
              <div {...getSafeInnerHtml(product.desc || '')} />
            </ShowMore>
          </S.ProductDesc>
        </S.ImageColumn>
        <S.DescriptionColumn>
          <ProductDetailInfo product={product} />
          <S.SkuListWrapper>
            <Typography variant="body1Bold">{t('selectDenomation')}</Typography>
            <DonationOptions
              charity={product}
              selectedSkus={selectedSkus}
              setSelectedSkus={setSelectedSkus}
            />
            {product.shortDesc && secondaryType === PRODUCT_TYPES.CHARITY && (
              <Typography variant="caption2" emphasis="weak">
                <div {...getSafeInnerHtml(product.shortDesc || '')} />
              </Typography>
            )}
          </S.SkuListWrapper>
        </S.DescriptionColumn>
      </S.SecondaryModalBody>
      {(product.meta?.incommAssets?.termsconditions?.text ||
        product.meta?.incommAssets?.legaldisclaimer?.text) && (
        <S.FooterText variant="caption1" color="fg" emphasis="weak">
          <Trans
            i18nKey="giftCardModalFooter"
            components={{
              a1: (
                <Button
                  variant="link"
                  color="fg"
                  size="sm"
                  onClick={showPurchaseDisclaimerDialog}
                />
              ),
              a2: (
                <Button variant="link" color="fg" size="sm" onClick={showTermsAndConditionDialog} />
              )
            }}
          />
        </S.FooterText>
      )}
    </Modal>
  );
}

SecondaryProductDetailModal.propTypes = {
  product: PropTypes.object.isRequired,
  isSelectedProduct: PropTypes.bool.isRequired,
  initialSelectedSkus: PropTypes.array.isRequired,
  secondaryType: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onBackClick: PropTypes.func.isRequired,
  onOk: PropTypes.func,
  onUpdateProduct: PropTypes.func,
  isProductsRemovable: PropTypes.bool,
  featureType: PropTypes.string,
  isDraft: PropTypes.bool
};

export { SecondaryProductDetailModal };
